import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, HardHat, AlertTriangle, X } from 'lucide-react';
import { cantieri as cantieriApi, clienti as clientiApi } from '../utils/api';
import CantiereDashboardModal from '../components/cantieri/CantiereDashboardModal';
import LimiteCantieriModal from '../components/cantieri/LimiteCantieriModal';
import { limiteCantieriDaErrore } from '../components/cantieri/limiteCantieri';
import NomeGiaPresenteModal from '../components/shared/NomeGiaPresenteModal';
import { nomeGiaPresenteDaErrore } from '../components/shared/nomeGiaPresente';
import { STATO_META, STATI_LIST, STATI_ATTIVI, isCantiereInRitardo } from '../constants/cantieriStati';
import useIsMobile from '../hooks/useIsMobile';
import { useListMode, ListModeToggle } from '../components/shared/ListView';
import DataCard from '../components/shared/DataCard';
import ScrollFade from '../components/shared/ScrollFade';
import RankingTable, { renderBarraCoda } from '../components/shared/RankingTable';

// Etichette delle viste derivate (?vista=) — CRITERI, non campi `stato`.
const VISTA_META = {
  attivi:  'Cantieri attivi',
  ritardo: 'Cantieri in ritardo',
};

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtEur = (n) =>
  n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`;

const fmtData = (d) => {
  if (!d) return '—';
  const [y, m, g] = d.split('-');
  return `${g}/${m}/${y}`;
};

// STATO_META / STATI_LIST: fonte unica in ../constants/cantieriStati (DBT-050),
// allineata al backend e condivisa con il dettaglio (StatoTab).

const PRIORITA_META = {
  bassa:  { label: 'Bassa',  color: 'var(--ds-muted)' },
  media:  { label: 'Media',  color: 'var(--warn)' },
  alta:   { label: 'Alta',   color: 'var(--ds-danger)' },
};

const TIPOLOGIA_LABEL = {
  ristrutturazione: 'Ristrutturazione',
  elettrico:        'Impianto elettrico',
  idraulico:        'Impianto idraulico',
  tetto:            'Tetto e coperture',
  nuovo_edificio:   'Nuovo edificio',
  altro:            'Altro',
};

const TIPOLOGIE_LIST = ['ristrutturazione', 'elettrico', 'idraulico', 'tetto', 'nuovo_edificio', 'altro'];
const PRIORITA_LIST = ['bassa', 'media', 'alta'];

const FORM_EMPTY = {
  titolo: '', indirizzo: '', citta: '', provincia: '',
  tipologia: 'altro', stato: 'nuovo', priorita: 'media',
  data_inizio: '', data_fine_stimata: '',
  valore_commessa: '', note: '', cliente: '',
};

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';

// ── Badge stato ────────────────────────────────────────────────────────────
function StatoBadge({ stato }) {
  const m = STATO_META[stato] || { label: stato, color: 'var(--ds-muted)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      fontSize: 12, fontWeight: 500,
      background: `color-mix(in srgb, ${m.color} 14%, transparent)`, color: m.color,
    }}>{m.label}</span>
  );
}

// ── Voce 4 — icona "in ritardo" ──────────────────────────────────────────────
// Stessa definizione della card Home e del filtro ?vista=ritardo
// (isCantiereInRitardo, source unica). Accostata al badge stato in lista.
function RitardoBadge({ cantiere }) {
  if (!isCantiereInRitardo(cantiere)) return null;
  return (
    <span title="In ritardo" aria-label="In ritardo" style={{
      display: 'inline-flex', alignItems: 'center', color: 'var(--ds-danger)',
      marginLeft: 6, verticalAlign: 'middle',
    }}>
      <AlertTriangle size={14} strokeWidth={2} />
    </span>
  );
}

// ── Modal (usato solo per il form) ─────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 28px', borderBottom: '1px solid var(--ds-border)',
        }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 22, color: 'var(--ds-muted)', lineHeight: 1, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Form cantiere ──────────────────────────────────────────────────────────
function CantiereForm({ initial, clientiList, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...FORM_EMPTY, ...initial });
  const [importBanner, setImportBanner] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onClienteChange = (id) => {
    set('cliente', id);
    if (id) {
      const c = clientiList.find((x) => x.id === id);
      if (c && (c.indirizzo || c.citta || c.provincia)) setImportBanner(c);
      else setImportBanner(null);
    } else {
      setImportBanner(null);
    }
  };

  const importaDatiCliente = () => {
    if (!importBanner) return;
    if (importBanner.indirizzo) set('indirizzo', importBanner.indirizzo);
    if (importBanner.citta)     set('citta',     importBanner.citta);
    if (importBanner.provincia) set('provincia',  importBanner.provincia);
    setImportBanner(null);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-base)',
    border: '1px solid var(--ds-border)', fontSize: 14,
    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
    background: 'var(--ds-bg)', color: 'var(--ink)',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 6 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Titolo cantiere *</label>
        <input required data-campo-nome="true" style={inputStyle} value={form.titolo}
          onChange={(e) => set('titolo', e.target.value)} placeholder="Es. Ristrutturazione Via Roma 12" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Cliente</label>
        <select style={inputStyle} value={form.cliente} onChange={(e) => onClienteChange(e.target.value)}>
          <option value="">— Nessun cliente —</option>
          {clientiList.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
        {importBanner && (
          <div style={{ marginTop: 8, padding: '10px 14px', background: 'color-mix(in srgb, var(--brand) 10%, transparent)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--ink)' }}>
              Importare l'indirizzo di <strong>{importBanner.nome}</strong>?
            </span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button type="button" onClick={importaDatiCliente}
                style={{ fontSize: 12, padding: '10px 16px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
                Si
              </button>
              <button type="button" onClick={() => setImportBanner(null)}
                style={{ fontSize: 12, padding: '10px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontWeight: 500 }}>
                No
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Indirizzo</label>
        <input style={inputStyle} value={form.indirizzo}
          onChange={(e) => set('indirizzo', e.target.value)} placeholder="Via, numero civico" />
      </div>

      <div style={row2}>
        <div>
          <label style={labelStyle}>Città</label>
          <input style={inputStyle} value={form.citta}
            onChange={(e) => set('citta', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Provincia</label>
          <input style={inputStyle} value={form.provincia} maxLength={5}
            onChange={(e) => set('provincia', e.target.value)} placeholder="Es. MI" />
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={labelStyle}>Tipologia</label>
          <select style={inputStyle} value={form.tipologia} onChange={(e) => set('tipologia', e.target.value)}>
            {TIPOLOGIE_LIST.map((t) => (
              <option key={t} value={t}>{TIPOLOGIA_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Priorità</label>
          <select style={inputStyle} value={form.priorita} onChange={(e) => set('priorita', e.target.value)}>
            {PRIORITA_LIST.map((p) => (
              <option key={p} value={p}>{PRIORITA_META[p].label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={labelStyle}>Stato</label>
          <select style={inputStyle} value={form.stato} onChange={(e) => set('stato', e.target.value)}>
            {STATI_LIST.map((s) => (
              <option key={s} value={s}>{STATO_META[s].label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Valore commessa (€)</label>
          <input style={inputStyle} type="number" min="0" step="0.01"
            value={form.valore_commessa} onChange={(e) => set('valore_commessa', e.target.value)}
            placeholder="0" />
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={labelStyle}>Data inizio</label>
          <input style={inputStyle} type="date" value={form.data_inizio}
            onChange={(e) => set('data_inizio', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Data fine stimata</label>
          <input style={inputStyle} type="date" value={form.data_fine_stimata}
            onChange={(e) => set('data_fine_stimata', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Note</label>
        <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
          value={form.note} onChange={(e) => set('note', e.target.value)}
          placeholder="Note aggiuntive sul cantiere..." />
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{
          padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
          background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
        }}>Annulla</button>
        <button type="submit" disabled={saving} style={{
          padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none',
          background: 'var(--brand)',
          color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500,
          opacity: saving ? 0.6 : 1,
        }}>{saving ? 'Salvataggio…' : (initial?.id ? 'Salva modifiche' : 'Crea cantiere')}</button>
      </div>
    </form>
  );
}

// ── Pagina principale ──────────────────────────────────────────────────────
export default function Cantieri() {
  const isMobile = useIsMobile();
  const { isCards, mode, setMode } = useListMode('cantieri', isMobile);
  const location = useLocation();
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [stats, setStats] = useState(null);
  const [clientiList, setClientiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);

  // Filtri
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  // Vista derivata (?vista=attivi|ritardo da Home). CRITERIO, non un campo stato:
  // vive separata da filtroStato e i due si escludono (vedi handler filtri).
  const [filtroVista, setFiltroVista] = useState('');

  // Modal
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // cantiere da modificare
  const [saving, setSaving] = useState(false);
  // CAP-018 — limite cantieri raggiunto: modale, mai l'alert col JSON grezzo.
  const [limiteInfo, setLimiteInfo] = useState(null);
  // DBT-157 — creazione o rinomina verso un titolo già usato: modale dedicato.
  const [nomeInfo, setNomeInfo] = useState(null);

  // Dashboard hub modal
  const [dashboardId, setDashboardId] = useState(null);
  const [dashboardTab, setDashboardTab] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      // page_size ampio (come Home): il filtro vista e il ranking sono
      // client-side e devono vedere l'intera lista, non solo la 1ª pagina da 20.
      const params = { page_size: 200 };
      if (search) params.search = search;
      if (filtroStato) params.stato = filtroStato;
      const [listRes, statsRes] = await Promise.all([
        cantieriApi.list(params),
        cantieriApi.statistiche(),
      ]);
      setLista(listRes.data.results ?? listRes.data);
      setStats(statsRes.data);
    } catch {
      setErrore('Errore nel caricamento cantieri');
    } finally {
      setLoading(false);
    }
  }, [search, filtroStato]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    clientiApi.list().then((r) => setClientiList(r.data.results ?? r.data)).catch(() => {});
  }, []);

  const apriNuovo = () => { setEditing(null); setShowForm(true); };
  const apriModifica = (c) => { setEditing(c); setShowForm(true); };

  // Apri form automaticamente se navigazione da sidebar (+Nuovo Cantiere)
  useEffect(() => {
    if (location.pathname === '/cantieri/nuovo') apriNuovo();
  }, [location.pathname]); // eslint-disable-line

  // Feature2 — apri il dashboard cantiere su hash #cantiere-{uuid}
  // (redirect dopo "Apri cantiere da preventivo")
  useEffect(() => {
    const m = location.hash.match(/^#cantiere-([0-9a-fA-F-]{36})$/);
    if (m) setDashboardId(m[1]);
  }, [location.hash]);

  // Voce 2 UX — route /cantieri/:id apre la scheda, confluendo nello STESSO
  // setDashboardId dell'hash (nessun terzo pattern). Il modal gestisce da sé
  // hash+history. Sistema i deep-link rotti: Home "Dettaglio →", cantieri
  // recenti, "Cosa fare oggi" atterravano sulla lista senza aprire nulla.
  useEffect(() => {
    const m = location.pathname.match(/^\/cantieri\/([0-9a-fA-F-]{36})$/);
    if (m) {
      setDashboardId(m[1]);
      // Deep-link da centro notifiche: ?tab=piano apre la tab Piano Lavori
      setDashboardTab(new URLSearchParams(location.search).get('tab') || null);
    }
  }, [location.pathname, location.search]);

  // Voce 2 UX — vista derivata dalla card Home (?vista=attivi|ritardo).
  // È un CRITERIO, non un campo stato: convive con filtroStato ma i due si
  // escludono a vicenda quando l'utente ne sceglie uno dai controlli.
  useEffect(() => {
    const v = new URLSearchParams(location.search).get('vista');
    setFiltroVista(v === 'attivi' || v === 'ritardo' ? v : '');
  }, [location.search]);

  const salva = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.cliente) delete payload.cliente;
      if (!payload.data_inizio) delete payload.data_inizio;
      if (!payload.data_fine_stimata) delete payload.data_fine_stimata;
      if (!payload.valore_commessa) delete payload.valore_commessa;

      if (editing) {
        const r = await cantieriApi.update(editing.id, payload);
        setLista((prev) => prev.map((c) => c.id === editing.id ? r.data : c));
      } else {
        const r = await cantieriApi.create(payload);
        setLista((prev) => [r.data, ...prev]);
      }
      setShowForm(false);
      setEditing(null);
      cantieriApi.statistiche().then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      const limite = limiteCantieriDaErrore(e);
      if (limite) { setLimiteInfo(limite); return; }
      const ng = nomeGiaPresenteDaErrore(e);
      if (ng) { setNomeInfo(ng); return; }
      alert('Errore nel salvataggio: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (id) => {
    try {
      await cantieriApi.delete(id);
      carica();
    } catch {
      alert('Errore nell\'eliminazione');
    }
  };

  // UX-RT-051 — sync ottimistico dal tab Stato della modale: riflette subito il
  // nuovo stato sul badge di riga in lista. La mutazione server + rollback + toast
  // vivono in StatoTab; qui solo il riflesso locale (chiamato anche in rollback).
  const handleStatoChanged = useCallback((id, stato) => {
    setLista((l) => l.map((c) => (c.id === id ? { ...c, stato } : c)));
  }, []);

  // Riconcilia i KPI aggregati (per_stato/valori) alla chiusura della modale.
  const refreshStats = useCallback(() => {
    cantieriApi.statistiche().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Lista filtrata localmente sul dato già filtrato dal server (doppia sicurezza).
  // filtroStato = campo `stato`; filtroVista = criterio derivato (attivi/ritardo).
  const listaFiltrata = lista.filter((c) => {
    if (filtroStato && c.stato !== filtroStato) return false;
    if (filtroVista === 'attivi' && !STATI_ATTIVI.includes(c.stato)) return false;
    if (filtroVista === 'ritardo' && !isCantiereInRitardo(c)) return false;
    return true;
  });

  // Ranking cantieri per valore commessa (top 5, % su totale globale)
  const totaleGlobale = lista.reduce((s, x) => s + parseFloat(x.valore_commessa || 0), 0);
  const rankingCantieri = lista
    .filter(c => parseFloat(c.valore_commessa) > 0)
    .sort((a, b) => parseFloat(b.valore_commessa || 0) - parseFloat(a.valore_commessa || 0))
    .slice(0, 5)
    .map((c, i) => {
      const val = parseFloat(c.valore_commessa || 0);
      return {
        pos: i + 1,
        nome: c.titolo,
        valore: Math.round(val),
        pct: totaleGlobale > 0 ? (val / totaleGlobale * 100).toFixed(1) : '0',
      };
    });

  // Corpo lista: loading → errore → empty → (mobile: card | desktop: tabella).
  // Condiviso dai due layout; il badge card legge c.stato dallo stesso
  // listaFiltrata → eredita il sync ottimistico di handleStatoChanged (DBT-050).
  const listaBody = loading ? (
    <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-muted)' }}>Caricamento…</div>
  ) : errore ? (
    <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-danger)' }}>{errore}</div>
  ) : listaFiltrata.length === 0 ? (
    <div style={{
      textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)',
      borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW,
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><HardHat size={44} strokeWidth={1.5} /></div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>Nessun cantiere trovato</div>
      <div style={{ fontSize: 14, color: 'var(--ds-muted)', marginBottom: 24 }}>
        {search || filtroStato || filtroVista ? 'Prova a cambiare i filtri di ricerca' : 'Crea il tuo primo cantiere per iniziare'}
      </div>
      {!search && !filtroStato && !filtroVista && (
        <button onClick={apriNuovo} style={{
          padding: '11px 24px', borderRadius: 'var(--radius-base)', border: 'none',
          background: 'var(--brand)',
          color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
        }}>+ Nuovo cantiere</button>
      )}
    </div>
  ) : isCards ? (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 10 : 14 }}>
      {listaFiltrata.map((c) => (
        <DataCard
          key={c.id}
          title={c.titolo}
          subtitle={[c.cliente_nome, c.citta].filter(Boolean).join(' · ') || null}
          onClick={() => setDashboardId(c.id)}
          badge={<span style={{ display: 'inline-flex', alignItems: 'center' }}><StatoBadge stato={c.stato} /><RitardoBadge cantiere={c} /></span>}
          meta={[
            { label: 'Tipologia', value: TIPOLOGIA_LABEL[c.tipologia] || c.tipologia },
            { label: 'Valore', value: fmtEur(c.valore_commessa) },
            c.fatturato_totale > 0 ? { label: 'Fatturato', value: fmtEur(c.fatturato_totale) } : null,
            c.da_incassare > 0 ? { label: 'Da incassare', value: fmtEur(c.da_incassare) } : null,
          ]}
        />
      ))}
    </div>
  ) : (
    <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Elenco Cantieri</span>
      </div>
      {/* Voce 16 — la tabella (9 colonne nowrap) scrolla in orizzontale nella
          fascia 768-1100px invece di essere tagliata; il fade segnala l'altro. */}
      <ScrollFade>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
            {['Cantiere', 'Cliente', 'Città', 'Stato', 'Valore', 'Costi', 'Margine', 'Fatturato', 'Da incassare'].map((h) => (
              <th key={h} style={{
                padding: '12px 16px', textAlign: 'left', fontSize: 11,
                fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase',
                letterSpacing: '0.05em', whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listaFiltrata.map((c, i) => (
            <tr
              key={c.id}
              onClick={() => setDashboardId(c.id)}
              style={{
                borderTop: '1px solid var(--ds-border)', cursor: 'pointer',
                background: ROW_BG(i),
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = ROW_HOVER}
              onMouseLeave={(e) => e.currentTarget.style.background = ROW_BG(i)}
            >
              <td style={{ padding: '13px 16px' }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--ink)' }}>{c.titolo}</div>
                <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>{TIPOLOGIA_LABEL[c.tipologia] || c.tipologia}</div>
              </td>
              <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{c.cliente_nome || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
              <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{c.citta || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
              <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}><StatoBadge stato={c.stato} /><RitardoBadge cantiere={c} /></td>
              <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtEur(c.valore_commessa)}</td>
              <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ds-danger)', whiteSpace: 'nowrap' }}>
                {c.totale_costi > 0 ? fmtEur(c.totale_costi) : <span style={{ color: 'var(--ds-border)', fontWeight: 400 }}>—</span>}
              </td>
              <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                {(() => {
                  const v = parseFloat(c.valore_commessa) || 0;
                  const costi = parseFloat(c.totale_costi) || 0;
                  if (!v && !costi) return <span style={{ color: 'var(--ds-border)' }}>—</span>;
                  const margine = v - costi;
                  const overBudget = costi > v && v > 0;
                  return (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: overBudget ? 'var(--ds-danger)' : 'var(--ok)' }}>
                        {overBudget ? '▼ ' : margine > 0 ? '▲ ' : ''}{fmtEur(Math.abs(margine))}
                      </div>
                      {v > 0 && (
                        <div style={{ fontSize: 9, color: 'var(--ds-muted)', marginTop: 2 }}>
                          {overBudget ? 'Sforato' : `${Math.round((margine / v) * 100)}% margine`}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </td>
              <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', color: c.fatturato_totale > 0 ? 'var(--brand)' : 'var(--ds-muted)' }}>
                {c.fatturato_totale > 0 ? fmtEur(c.fatturato_totale) : <span style={{ color: 'var(--ds-border)', fontWeight: 400 }}>—</span>}
              </td>
              <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', color: c.da_incassare > 0 ? 'var(--warn)' : 'var(--ds-muted)' }}>
                {c.da_incassare > 0 ? fmtEur(c.da_incassare) : <span style={{ color: 'var(--ds-border)', fontWeight: 400 }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </ScrollFade>
    </div>
  );

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Cantieri</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>
            Gestione e monitoraggio dei tuoi cantieri
          </p>
        </div>
        <button data-testid="btn-nuovo-cantiere" onClick={apriNuovo} style={{
          padding: '11px 22px', borderRadius: 'var(--radius-base)', border: 'none',
          background: 'var(--brand)',
          color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 14px rgba(46,160,132,0.35)',
        }}>+ Nuovo cantiere</button>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 14, marginBottom: 28,
        }}>
          {[
            { label: 'Totale', value: stats.totale, color: 'var(--brand)' },
            { label: 'In corso', value: stats.per_stato?.in_corso || 0, color: 'var(--zone-blue)' },
            { label: 'Nuovo', value: stats.per_stato?.nuovo || 0, color: 'var(--zone-violet)' },
            { label: 'Completati', value: stats.per_stato?.completato || 0, color: 'var(--ok)' },
            { label: 'Sospesi', value: stats.per_stato?.sospeso || 0, color: 'var(--ds-danger)' },
            { label: 'Valore totale', value: fmtEur(stats.valore_totale), color: 'var(--zone-slate)', big: true },
            { label: 'Fatturato', value: fmtEur(stats.fatturato_totale), color: 'var(--zone-acqua)', big: true },
            { label: 'Da incassare', value: fmtEur(stats.da_incassare), color: 'var(--warn)', big: true,
              hint: `In corso: ${fmtEur(stats.da_incassare_inviata || 0)} · Scadute: ${fmtEur(stats.da_incassare_scaduta || 0)}` },
            { label: 'Da fatturare', value: fmtEur((stats.valore_totale || 0) - (stats.fatturato_totale || 0)), color: 'var(--zone-magenta)', big: true },
          ].map((s) => (
            <div key={s.label} title={s.hint || undefined} style={{
              background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px',
              border: '1px solid var(--ds-border)', borderTop: `3px solid ${s.color}`, boxShadow: CARD_SHADOW,
              cursor: s.hint ? 'help' : 'default',
            }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: s.big ? 18 : 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{s.value}</div>
              {s.hint && (stats.da_incassare_scaduta || 0) > 0 && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--warn)', marginTop: 4 }}>di cui scadute {fmtEur(stats.da_incassare_scaduta)}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Filtri */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}><Search size={16} strokeWidth={2} /></span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per titolo, città…"
            style={{
              width: '100%', padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)',
              border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none',
              boxSizing: 'border-box', fontFamily: 'inherit',
              background: 'var(--ds-bg)', color: 'var(--ink)',
            }}
          />
        </div>

        {/* Filtro stato — "Tutti" azzera sia il campo stato sia la vista derivata */}
        <button onClick={() => { setFiltroStato(''); if (filtroVista) navigate('/cantieri', { replace: true }); }} style={{
          padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          background: (!filtroStato && !filtroVista) ? 'var(--brand)' : 'var(--ds-bg)',
          color: (!filtroStato && !filtroVista) ? '#fff' : 'var(--ds-text)',
          border: (!filtroStato && !filtroVista) ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
        }}>Tutti</button>
        <select
          value={filtroStato}
          onChange={(e) => {
            const v = e.target.value;
            setFiltroStato(v);
            // scegliere uno stato esclude la vista derivata (?stato vs ?vista)
            if (v && filtroVista) navigate('/cantieri', { replace: true });
          }}
          style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', outline: 'none', fontFamily: 'inherit' }}
        >
          <option value="">Filtra stato…</option>
          {STATI_LIST.map((s) => <option key={s} value={s}>{STATO_META[s].label}</option>)}
        </select>

        {/* Chip vista derivata (?vista=) — removibile, pulisce anche l'URL */}
        {filtroVista && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 8px 8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 500,
            background: 'color-mix(in srgb, var(--brand) 14%, transparent)',
            color: 'var(--brand)', border: '1px solid color-mix(in srgb, var(--brand) 30%, transparent)',
          }}>
            {VISTA_META[filtroVista]}
            <button onClick={() => navigate('/cantieri', { replace: true })} aria-label="Rimuovi filtro vista" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer',
              background: 'color-mix(in srgb, var(--brand) 20%, transparent)', color: 'var(--brand)',
            }}><X size={14} strokeWidth={2.5} /></button>
          </span>
        )}

        {/* Toggle Card/Tabella — solo desktop (su mobile è sempre Card) */}
        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            <ListModeToggle mode={mode} setMode={setMode} />
          </div>
        )}
      </div>

      {/* Contenuto — layout 2 colonne (desktop) / stack (mobile). listaBody
          condiviso; ramo mobile additivo, ramo desktop invariato. */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {listaBody}
          <RankingTable
            title="Top Cantieri per Valore"
            colNome="CANTIERE"
            colValore="VALORE"
            colCoda="% SUL TOTALE"
            rows={rankingCantieri}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 24, alignItems: 'start' }}>
          {/* Colonna sinistra: tabella */}
          <div>{listaBody}</div>
          {/* Colonna destra: Top Cantieri per Valore */}
          <RankingTable
            title="Top Cantieri per Valore"
            colNome="CANTIERE"
            colValore="VALORE"
            colCoda="% SUL TOTALE"
            rows={rankingCantieri}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      )}

      {/* Modal form */}
      {showForm && (
        <Modal title={editing ? 'Modifica cantiere' : 'Nuovo cantiere'} onClose={() => setShowForm(false)}>
          <CantiereForm
            initial={editing || {}}
            clientiList={clientiList}
            onSave={salva}
            onCancel={() => setShowForm(false)}
            saving={saving}
          />
        </Modal>
      )}

      {/* Dashboard Hub Modal */}
      <CantiereDashboardModal
        cantiereId={dashboardId}
        isOpen={!!dashboardId}
        initialTab={dashboardTab}
        onClose={() => { setDashboardId(null); setDashboardTab(null); refreshStats(); }}
        onEdit={(c) => { setDashboardId(null); apriModifica(c); }}
        onStatoChanged={handleStatoChanged}
      />

      <LimiteCantieriModal
        info={limiteInfo}
        onClose={() => setLimiteInfo(null)}
        onVaiCantieri={() => { setShowForm(false); setEditing(null); }}
      />
      <NomeGiaPresenteModal
        info={nomeInfo}
        onClose={() => setNomeInfo(null)}
        onApriEsistente={(id) => { setShowForm(false); setEditing(null); setDashboardId(id); }}
      />
    </div>
  );
}
